// Clients list — workspace-scoped. Latinos Salud shows Client ID + Athena ID;
// Standard Calyvra shows demographic names.
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import portalApi from '../lib/api';
import { Users, Search, Loader2, Plus, IdCard, X, Lock, Send } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

export function ClientsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activeWorkspace, hasPermission, isCalyvraAdmin } = useAuth();
  const [clients, setClients] = useState([]);
  const [restrictedMatches, setRestrictedMatches] = useState([]);
  const [reqTarget, setReqTarget] = useState(null);
  const [reqReason, setReqReason] = useState('');
  const [submittingReq, setSubmittingReq] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAgent, setBulkAgent] = useState('');
  const [bulkMode, setBulkMode] = useState('primary');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkRequired, setBulkRequired] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [agents, setAgents] = useState([]);
  const [constants, setConstants] = useState(null);
  const [filters, setFilters] = useState(() => ({
    product_type: '', product_category: searchParams.get('category') || '',
    agent_id: searchParams.get('agent_id') || '', carrier: '', state: '', policy_status: searchParams.get('status') || '',
  }));

  const isSimplified = activeWorkspace?.client_profile === 'simplified';
  const canManage = hasPermission('clients.manage') || isCalyvraAdmin;
  const agentFilterName = searchParams.get('agent_name') || '';
  const clearAgentFilter = () => {
    setFilters((p) => ({ ...p, agent_id: '' }));
    navigate('/portal/clients', { replace: true });
  };

  useEffect(() => {
    if (isSimplified) return;
    portalApi.getAgents().then(setAgents).catch(() => {});
    portalApi.getConstants().then(setConstants).catch(() => {});
  }, [isSimplified]);

  const setFilter = (k, v) => setFilters((p) => ({ ...p, [k]: v === 'all' ? '' : v }));

  const load = useCallback(async () => {
    if (!activeWorkspace) return;
    setLoading(true);
    try {
      const params = { workspace_id: activeWorkspace.id, q };
      if (filters.agent_id) params.agent_id = filters.agent_id;
      if (!isSimplified) Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const data = await portalApi.getClients(params);
      setClients(data.items || []);
      setRestrictedMatches(data.restricted_matches || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspace, q, filters, isSimplified]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSel = (cid) => setSelected((p) => { const n = new Set(p); n.has(cid) ? n.delete(cid) : n.add(cid); return n; });

  const submitBulkAssign = async () => {
    setAssigning(true);
    try {
      await portalApi.bulkAssignClients({
        client_ids: [...selected], agent_id: bulkAgent || null, mode: bulkMode,
        note: bulkNote.trim() || null, action_required: bulkRequired,
      });
      toast.success(`${selected.size} client(s) assigned`);
      setBulkOpen(false); setSelected(new Set()); setBulkAgent(''); setBulkNote(''); setBulkRequired(false);
      load();
    } catch (err) { toast.error(err.message); } finally { setAssigning(false); }
  };

  const submitRequest = async () => {
    if (!reqTarget) return;
    setSubmittingReq(true);
    try {
      await portalApi.requestClientAccess(reqTarget.id, reqReason);
      toast.success('Access request sent to your administrator');
      setReqTarget(null);
      setReqReason('');
      setRestrictedMatches((prev) => prev.map((m) => m.id === reqTarget.id ? { ...m, request_pending: true } : m));
    } catch (err) {
      toast.error(err.message || 'Could not send request');
    } finally {
      setSubmittingReq(false);
    }
  };

  const relFor = (c) => c.workspaces?.find((r) => r.workspace_id === activeWorkspace?.id && r.status === 'Active')
    || c.workspaces?.find((r) => r.workspace_id === activeWorkspace?.id);
  const displayName = (c) => {
    const d = c.demographics || {};
    const name = [d.first_name, d.last_name].filter(Boolean).join(' ');
    return name || (isSimplified ? '—' : 'Unnamed client');
  };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="h-6 w-6 text-sky-500" /> Clients{filters.agent_id && agentFilterName ? ` — ${agentFilterName}` : ''}
          </h1>
          <p className="text-slate-500 text-sm">
            {filters.agent_id
              ? `${total} assigned client${total === 1 ? '' : 's'}${agentFilterName ? ` · ${agentFilterName}` : ''}`
              : `${activeWorkspace?.name} · ${total} client${total === 1 ? '' : 's'}`}
          </p>
        </div>
        {canManage && !isSimplified && (
          <Button onClick={() => navigate('/portal/clients/new')} data-testid="new-client-btn"><Plus className="h-4 w-4 mr-2" /> New Client</Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by Client ID, Athena ID, name..." className="pl-9" data-testid="clients-search" />
      </div>

      {filters.agent_id && (
        <div className="flex items-center gap-2" data-testid="agent-filter-banner">
          <Badge className="bg-sky-100 text-sky-700 h-9 px-3 gap-1 text-sm">
            Showing clients assigned to {agentFilterName || 'this agent'}
            <button onClick={clearAgentFilter} className="ml-1 hover:text-sky-900" data-testid="clear-agent-filter-btn">
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        </div>
      )}

      {!isSimplified && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="client-filters">
          {filters.product_category && (
            <Badge className="bg-sky-100 text-sky-700 h-9 px-3 gap-1" data-testid="active-category-chip">
              {filters.product_category}
              <button onClick={() => setFilter('product_category', '')} className="ml-1 hover:text-sky-900" data-testid="clear-category-btn">
                <X className="h-3.5 w-3.5" />
              </button>
            </Badge>
          )}
          <Select value={filters.product_type || 'all'} onValueChange={(v) => setFilter('product_type', v)}>
            <SelectTrigger className="w-[190px]" data-testid="filter-product"><SelectValue placeholder="All Products" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Products</SelectItem>{(constants?.product_types || []).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          {isCalyvraAdmin && (
          <Select value={filters.agent_id || 'all'} onValueChange={(v) => setFilter('agent_id', v)}>
            <SelectTrigger className="w-[170px]" data-testid="filter-agent"><SelectValue placeholder="All Agents" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Agents</SelectItem>{agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
          )}
          <Select value={filters.carrier || 'all'} onValueChange={(v) => setFilter('carrier', v)}>
            <SelectTrigger className="w-[160px]" data-testid="filter-carrier"><SelectValue placeholder="All Carriers" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Carriers</SelectItem>{(constants?.insurance_carriers || ['Oscar','Aetna','Humana','Cigna','UnitedHealthcare','Ambetter','Florida Blue']).map(cr => <SelectItem key={cr} value={cr}>{cr}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.state || 'all'} onValueChange={(v) => setFilter('state', v)}>
            <SelectTrigger className="w-[150px]" data-testid="filter-state"><SelectValue placeholder="All States" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All States</SelectItem>{(constants?.us_states || []).map(st => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.policy_status || 'all'} onValueChange={(v) => setFilter('policy_status', v)}>
            <SelectTrigger className="w-[160px]" data-testid="filter-policy-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{(constants?.policy_statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      {isCalyvraAdmin && !isSimplified && selected.size > 0 && (
        <div className="flex items-center justify-between bg-sky-600 text-white rounded-xl px-5 py-3" data-testid="bulk-assign-bar">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">Clear</Button>
            <Button size="sm" className="bg-white text-sky-700 hover:bg-slate-100" onClick={() => setBulkOpen(true)} data-testid="bulk-assign-open-btn">Assign to agent</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-sky-500" /></div>
        ) : clients.length === 0 ? (
          <p className="text-center text-slate-400 py-16" data-testid="no-clients">No clients found in this workspace.</p>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {isCalyvraAdmin && !isSimplified && <th className="px-4 py-3 w-10"></th>}
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Calyvra Client ID</th>
                {isSimplified
                  ? <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Athena ID</th>
                  : <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Name</th>}
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Source</th>
                {!isSimplified && isCalyvraAdmin && <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Primary Agent</th>}
                {!isSimplified && <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">State</th>}
                {!isSimplified && <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Product(s)</th>}
                {!isSimplified && <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Carrier(s)</th>}
                {!isSimplified && <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Policy Status</th>}
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-6 py-3">Workspaces</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {clients.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`client-row-${c.calyvra_client_id}`}>
                  {isCalyvraAdmin && !isSimplified && (
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} data-testid={`select-client-${c.calyvra_client_id}`} />
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <Link to={`/portal/clients/${c.id}`} className="text-sky-600 font-medium hover:underline flex items-center gap-1">
                      <IdCard className="h-4 w-4" /> {c.calyvra_client_id}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{isSimplified ? (relFor(c)?.athena_id || '—') : displayName(c)}</td>
                  <td className="px-6 py-4 text-slate-500 text-sm">{c.source || '—'}</td>
                  {!isSimplified && isCalyvraAdmin && <td className="px-6 py-4 text-slate-600 text-sm" data-testid={`client-agent-${c.calyvra_client_id}`}>{c.primary_agent_name || '—'}</td>}
                  {!isSimplified && <td className="px-6 py-4 text-slate-600 text-sm">{c.state || '—'}</td>}
                  {!isSimplified && <td className="px-6 py-4" data-testid={`client-products-${c.calyvra_client_id}`}><div className="flex gap-1 flex-wrap">{(c.products || []).length ? c.products.map((p) => <Badge key={p} variant="outline" className="text-indigo-600 border-indigo-200">{p}</Badge>) : <span className="text-slate-400 text-sm">—</span>}</div></td>}
                  {!isSimplified && <td className="px-6 py-4 text-slate-600 text-sm">{(c.carriers || []).join(', ') || '—'}</td>}
                  {!isSimplified && <td className="px-6 py-4"><div className="flex gap-1 flex-wrap">{(c.policy_statuses || []).map((s) => <Badge key={s} className="bg-slate-100 text-slate-600">{s}</Badge>)}</div></td>}
                  <td className="px-6 py-4">
                    <div className="flex gap-1 flex-wrap">
                      {(c.workspaces || []).map((r) => (
                        <Badge key={r.workspace_id} className={r.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                          {r.workspace_name}{r.status === 'Active' ? '' : ` (${r.status})`}
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {restrictedMatches.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" data-testid="restricted-matches">
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-amber-600" />
            <h3 className="font-semibold text-amber-800 text-sm">Clients not assigned to you</h3>
          </div>
          <p className="text-xs text-amber-700 mb-3">These clients match your search but are not assigned to you. You can request access from your administrator.</p>
          <ul className="space-y-2">
            {restrictedMatches.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-amber-200 px-4 py-2.5" data-testid={`restricted-match-${m.id}`}>
                <span className="text-sm text-slate-700 font-medium">{m.name}</span>
                {m.request_pending ? (
                  <Badge className="bg-slate-100 text-slate-500" data-testid={`request-pending-${m.id}`}>Request pending</Badge>
                ) : (
                  <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => { setReqTarget(m); setReqReason(''); }} data-testid={`request-access-btn-${m.id}`}>
                    Request access
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={!!reqTarget} onOpenChange={(o) => { if (!o) setReqTarget(null); }}>
        <DialogContent data-testid="request-access-dialog">
          <DialogHeader>
            <DialogTitle>Request access to {reqTarget?.name}</DialogTitle>
            <DialogDescription>Your administrator will be notified and can grant you access to this client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs text-slate-500">Reason (optional)</label>
            <Textarea value={reqReason} onChange={(e) => setReqReason(e.target.value)} placeholder="Why do you need access to this client?" data-testid="request-access-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqTarget(null)} disabled={submittingReq}>Cancel</Button>
            <Button onClick={submitRequest} disabled={submittingReq} data-testid="submit-access-request-btn">
              {submittingReq ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!assigning) setBulkOpen(o); }}>
        <DialogContent data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign {selected.size} client{selected.size === 1 ? '' : 's'} to an agent</DialogTitle>
            <DialogDescription>The agent will be notified by email and in-app.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Agent</label>
              <Select value={bulkAgent || 'none'} onValueChange={(v) => setBulkAgent(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="bulk-agent-select"><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned (clear)</SelectItem>
                  {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Assignment type</label>
              <Select value={bulkMode} onValueChange={setBulkMode}>
                <SelectTrigger className="mt-1" data-testid="bulk-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary agent (replaces)</SelectItem>
                  <SelectItem value="authorized">Additional access (keep primary)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Note / action (optional)</label>
              <Textarea value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="e.g. Contact these clients this week" data-testid="bulk-note-input" />
              <label className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                <input type="checkbox" checked={bulkRequired} onChange={(e) => setBulkRequired(e.target.checked)} data-testid="bulk-required-check" />
                Mark as required action
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={assigning}>Cancel</Button>
            <Button onClick={submitBulkAssign} disabled={assigning} data-testid="bulk-assign-submit">
              {assigning ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ClientsPage;
